// Strings for the teacher's check-in workflow (Workflow I, 0017): plan,
// projector and live count, results, prediction, "You said, we did", private
// notes, schedules and calibration. Merged into STRINGS in strings.ts, so
// every language must define every key (checked by the type below). Finnish
// and Swedish are drafts and need native-speaker review before go-live.

type Shape<T> = { [K in keyof T]: T[K] extends (...a: infer A) => string ? (...a: A) => string : string };

const n = (count: number, one: string, many: string) => (count === 1 ? one : many);

export const teacherEn = {
  'ts.navCalibration': 'Your predictions',
  'ts.classesSub': 'Homeroom and subject classes',
  'ts.openSub': 'Started by you',
  'ts.noClasses': 'You have no classes yet. Your school admin assigns them.',
  'ts.whoSees':
    'Results are per check-in and only you see the check-ins you run. Your head of school sees grade and school totals only, never a class.',
  'ts.kind': 'Class',
  'ts.homeroom': 'Homeroom',
  'ts.subject': 'Subject class',
  'ts.size': 'Expected size',
  'ts.students': (c: number) => n(c, '1 student', `${c} students`),
  'ts.fc.likely': 'Likely to show results',
  'ts.fc.uncertain': 'May be too few',
  'ts.fc.unlikely': 'Too few to show results',
  'ts.fc.unknown': 'No forecast',
  'ts.fcHint.likely': 'Enough students usually answer for results to show.',
  'ts.fcHint.uncertain': 'Results show from 5 answers. Encourage everyone to take part.',
  'ts.fcHint.unlikely': 'Fewer than 5 answers are likely. They will still count toward the school total.',
  'ts.fcHint.unknown': 'Add the class size to get a forecast.',
  'ts.fcTitle': 'Participation forecast',
  'ts.fcHow': 'Based on the class size and how many answered your last check-ins here.',
  'ts.openUntil': (code: string, time: string) => `Open now · code ${code} · until ${time}`,
  'ts.colleagueOpen': 'A colleague has a check-in open for this class.',
  'ts.openProjector': 'Open projector view',
  'ts.plan': 'Plan check-in',
  'ts.lastResults': 'Last results',
  'ts.classPage': 'Class page',
  'ts.dueTitle': 'Scheduled now',
  'ts.day1': 'Monday',
  'ts.day2': 'Tuesday',
  'ts.day3': 'Wednesday',
  'ts.day4': 'Thursday',
  'ts.day5': 'Friday',
  'ts.day6': 'Saturday',
  'ts.day7': 'Sunday',
  'ts.minutes': (m: number) => `${m} min`,

  'ts.classTitle': (c: string) => `Class ${c}`,
  'ts.classTitleBare': 'Class',
  'ts.classGone': 'This class isn’t on your list. It may have been reassigned.',
  'ts.planTitle': 'Plan a check-in',
  'ts.length': 'Length',
  'ts.tag': 'What’s going on this week',
  'ts.tagNone': 'Nothing special',
  'ts.tag.normal': 'Normal week',
  'ts.tag.exam_week': 'Exam week',
  'ts.tag.after_holiday': 'After a holiday',
  'ts.tag.after_incident': 'After an incident',
  'ts.tag.trip_event': 'Trip or event',
  'ts.tagHint': 'Helps you read the results later. Your head of school sees only how often each is used, school-wide.',
  'ts.predictTitle': 'Before you start: how do you think the class is doing?',
  'ts.predictTitleShort': 'Your prediction',
  'ts.predictHint': 'Optional. Only you see it. You’ll compare it with the class’s answers afterwards.',
  'ts.predictMood': 'Expected mood',
  'ts.predictTopics': 'Topics you expect',
  'ts.predictClear': 'Clear prediction',
  'ts.predictAdd': 'Add a prediction',
  'ts.predictChange': 'Change prediction',
  'ts.noPrediction': 'No prediction yet.',
  'ts.draft': 'Draft',
  'ts.draftSet': (label: string) => `The question set “${label}” is a draft and not yet reviewed.`,

  'ts.schedTitle': 'Recurring check-in',
  'ts.schedHint': 'A reminder on your home screen at that time. You still start each check-in yourself.',
  'ts.schedDay': 'Day',
  'ts.schedTime': 'Start time',
  'ts.schedSave': '+ Add schedule',
  'ts.schedSaved': 'Schedule added.',
  'ts.schedEnd': 'Remove',
  'ts.schedEnded': 'Schedule removed.',

  'ts.histTitle': 'Your past check-ins',
  'ts.histHint': 'Only check-ins you ran. Each one is shown on its own.',
  'ts.histNone': 'No finished check-ins yet.',
  'ts.st.insufficient': 'Too few to show',
  'ts.st.suppressed_near_unanimous': 'Hidden',
  'ts.st.ok': 'Shown',
  'ts.youExpected': (m: number) => `You expected ${m}/5`,
  'ts.view': 'View',

  'ts.sessionTitle': 'Check-in',
  'ts.conductTitle': (c: string) => `Check-in open · ${c}`,
  'ts.reflectTitle': (c: string) => `Results · ${c}`,
  'ts.projLead': 'Scan the code or go to',
  'ts.timeLeft': (t: string) => `${t} left`,
  'ts.qrLabel': (code: string) => `QR code for the check-in link, code ${code}`,
  'ts.liveTitle': 'Answers so far',
  'ts.liveCount': (c: number) => n(c, 'answer', 'answers'),
  'ts.liveHint': 'Only the number shows while the check-in is open. Results show when it ends.',
  'ts.endsAt': (t: string) => `Ends at ${t}`,
  'ts.fullscreen': 'Full screen',
  'ts.extend': (m: number) => `+ ${m} min`,
  'ts.extended': (t: string) => `Extended until ${t}.`,
  'ts.atLimit': (m: number) => `This check-in is at your school’s ${m}-minute limit.`,
  'ts.slideToEnd': 'Slide to end check-in',
  'ts.slideToEndAria': 'End check-in. Press twice to end without sliding.',
  'ts.pressAgainToEnd': 'Press again to end',
  'ts.closeIntro': (c: number) =>
    `${c} ${n(c, 'student has', 'students have')} answered. Nobody can answer after it ends, and it can’t be reopened.`,
  'ts.closedNow': 'Check-in ended.',

  'ts.resultsTitle': 'What the class said',
  'ts.belowFloor': (total: number, floor: number) =>
    `${total} of ${floor} answers needed. Too few responses to show; they count toward the school total.`,
  'ts.byTopic': 'Mood by topic',
  'ts.crossMean': (mean: string, total: number) => `average ${mean}/5 · ${total} answers`,
  'ts.crossHidden': 'hidden',
  'ts.crossNote': 'A topic’s mood shows only when both that group and everyone else are big enough to show safely.',
  'ts.resultsWho': 'Only you see these results. Your head of school never sees a single class.',
  'ts.questionSet': (v: number) => `Question set v${v}`,
  'ts.compareTitle': 'Your prediction and the result',
  'ts.noPredictionMade': 'You didn’t make a prediction for this check-in.',
  'ts.compareHidden': 'The result is hidden, so there’s nothing to compare.',
  'ts.expected': 'You expected',
  'ts.classSaid': 'The class said',
  'ts.gap': 'Difference',
  'ts.gapClose': 'Close to what you expected',
  'ts.gapHigher': (d: string) => `You expected ${d} higher`,
  'ts.gapLower': (d: string) => `You expected ${d} lower`,
  'ts.expectedTopics': 'Topics you expected',
  'ts.onlyYou': 'Only you see your predictions and notes — not your head of school, not your school admin, not Zencera.',

  'ts.ysTitle': 'You said, we did',
  'ts.ysLabel': 'One line back to the class',
  'ts.ysPlaceholder': 'e.g. Lots of you mentioned exams, so we’re moving Friday’s quiz.',
  'ts.ysRule': 'Shown to the class on its next check-in. No names, and nothing about any one student.',
  'ts.charsLeft': (c: number) => `${c} characters left.`,
  'ts.ysPost': 'Post to the class',
  'ts.ysPosted': 'Posted. The class sees it on its next check-in.',
  'ts.ysShown': 'The class sees this on its next check-in (for up to 30 days).',
  'ts.ysWithdraw': 'Withdraw',
  'ts.ysWithdrawn': 'Withdrawn.',
  'ts.ysNotYet': 'You can reply once results are shown for this check-in.',

  'ts.noteTitle': 'Private notes',
  'ts.noteLabel': 'Notes for yourself',
  'ts.noteWho': 'Only you can read this. It isn’t shared with anyone, not even Zencera support, and is deleted if your account is removed.',
  'ts.noteSaved': 'Note saved.',
  'ts.save': 'Save',

  'ts.calSessions': 'Check-ins compared',
  'ts.calSessionsSub': 'With a prediction and shown results',
  'ts.calGap': 'Average difference',
  'ts.calNone': 'Make a prediction before your next check-in',
  'ts.calHigher': 'You tend to expect a better mood than the class reports',
  'ts.calLower': 'You tend to expect a lower mood than the class reports',
  'ts.calClose': 'Your predictions are close to what classes say',
  'ts.calByContext': 'By what was going on',
  'ts.calEmpty': 'Nothing to compare yet.',
  'ts.calN': (c: number) => n(c, '1 check-in', `${c} check-ins`),
  'ts.calHow': 'Difference = your expected mood minus the class average. + means you expected better than the class said.',

  'ci.youSaid': 'From your teacher',

  'err.not_your_class': 'This class or check-in isn’t yours, or you no longer teach the class.',
  'err.class_busy': 'A colleague has a check-in open for this class. Try again when it ends.',
  'err.session_finished': 'This check-in has already ended.',
  'err.session_open': 'Results show once the check-in has ended.',
  'err.session_limit': 'This check-in is already at your school’s time limit.',
  'err.results_not_shown': 'You can reply once this check-in’s results are shown.',
  'err.bad_length': 'Write 3 to 200 characters.',
  'err.bad_content': 'Plain text only: no links, email addresses or tags.',
  'err.already_posted': 'You already replied to this check-in.',
  'err.feature_off': 'This feature is switched off for now.',
  'err.too_many': 'You have the maximum number of schedules. Remove one first.',
};

export type TeacherStrings = Shape<typeof teacherEn>;

export const teacherFi: TeacherStrings = {
  'ts.navCalibration': 'Ennusteesi',
  'ts.classesSub': 'Luokanvalvojan ja aineen luokat',
  'ts.openSub': 'Itse aloittamasi',
  'ts.noClasses': 'Sinulla ei ole vielä luokkia. Koulun ylläpitäjä liittää ne sinulle.',
  'ts.whoSees':
    'Tulokset näytetään kyselykohtaisesti, ja vain sinä näet itse pitämäsi kyselyt. Rehtori näkee vain luokka-asteen ja koulun yhteenvedot, ei koskaan yksittäistä luokkaa.',
  'ts.kind': 'Luokka',
  'ts.homeroom': 'Oma luokka',
  'ts.subject': 'Aineen luokka',
  'ts.size': 'Oppilasmäärä',
  'ts.students': (c) => n(c, '1 oppilas', `${c} oppilasta`),
  'ts.fc.likely': 'Tulokset todennäköisesti näkyvät',
  'ts.fc.uncertain': 'Vastaajia voi olla liian vähän',
  'ts.fc.unlikely': 'Liian vähän vastaajia',
  'ts.fc.unknown': 'Ei ennustetta',
  'ts.fcHint.likely': 'Yleensä riittävän moni vastaa, jotta tulokset näkyvät.',
  'ts.fcHint.uncertain': 'Tulokset näkyvät 5 vastauksesta alkaen. Kannusta kaikkia vastaamaan.',
  'ts.fcHint.unlikely': 'Alle 5 vastausta on todennäköistä. Ne lasketaan silti koulun kokonaismäärään.',
  'ts.fcHint.unknown': 'Lisää luokan oppilasmäärä saadaksesi ennusteen.',
  'ts.fcTitle': 'Osallistumisennuste',
  'ts.fcHow': 'Perustuu luokan kokoon ja siihen, moniko vastasi viimeisimpiin kyselyihisi.',
  'ts.openUntil': (code, time) => `Auki · tunnus ${code} · klo ${time} asti`,
  'ts.colleagueOpen': 'Kollegalla on luokalle kysely auki.',
  'ts.openProjector': 'Avaa esitysnäkymä',
  'ts.plan': 'Suunnittele kysely',
  'ts.lastResults': 'Viimeisimmät tulokset',
  'ts.classPage': 'Luokan sivu',
  'ts.dueTitle': 'Aikataulussa nyt',
  'ts.day1': 'Maanantai',
  'ts.day2': 'Tiistai',
  'ts.day3': 'Keskiviikko',
  'ts.day4': 'Torstai',
  'ts.day5': 'Perjantai',
  'ts.day6': 'Lauantai',
  'ts.day7': 'Sunnuntai',
  'ts.minutes': (m) => `${m} min`,

  'ts.classTitle': (c) => `Luokka ${c}`,
  'ts.classTitleBare': 'Luokka',
  'ts.classGone': 'Tämä luokka ei ole listallasi. Se on ehkä siirretty toiselle opettajalle.',
  'ts.planTitle': 'Suunnittele kysely',
  'ts.length': 'Kesto',
  'ts.tag': 'Mitä tällä viikolla tapahtuu',
  'ts.tagNone': 'Ei mitään erityistä',
  'ts.tag.normal': 'Tavallinen viikko',
  'ts.tag.exam_week': 'Koeviikko',
  'ts.tag.after_holiday': 'Loman jälkeen',
  'ts.tag.after_incident': 'Tapahtuneen jälkeen',
  'ts.tag.trip_event': 'Retki tai tapahtuma',
  'ts.tagHint': 'Auttaa tulkitsemaan tuloksia myöhemmin. Rehtori näkee vain, kuinka usein kutakin käytetään koko koulussa.',
  'ts.predictTitle': 'Ennen aloitusta: miten luokalla mielestäsi menee?',
  'ts.predictTitleShort': 'Ennusteesi',
  'ts.predictHint': 'Valinnainen. Vain sinä näet sen. Vertaat sitä luokan vastauksiin jälkikäteen.',
  'ts.predictMood': 'Odotettu mieliala',
  'ts.predictTopics': 'Odottamasi aiheet',
  'ts.predictClear': 'Poista ennuste',
  'ts.predictAdd': 'Lisää ennuste',
  'ts.predictChange': 'Muuta ennustetta',
  'ts.noPrediction': 'Ei ennustetta vielä.',
  'ts.draft': 'Luonnos',
  'ts.draftSet': (label) => `Kysymyssarja ”${label}” on luonnos, eikä sitä ole vielä tarkistettu.`,

  'ts.schedTitle': 'Toistuva kysely',
  'ts.schedHint': 'Muistutus etusivullasi siihen aikaan. Aloitat jokaisen kyselyn silti itse.',
  'ts.schedDay': 'Päivä',
  'ts.schedTime': 'Alkamisaika',
  'ts.schedSave': '+ Lisää aikataulu',
  'ts.schedSaved': 'Aikataulu lisätty.',
  'ts.schedEnd': 'Poista',
  'ts.schedEnded': 'Aikataulu poistettu.',

  'ts.histTitle': 'Aiemmat kyselysi',
  'ts.histHint': 'Vain itse pitämäsi kyselyt. Jokainen näytetään erikseen.',
  'ts.histNone': 'Ei vielä päättyneitä kyselyitä.',
  'ts.st.insufficient': 'Liian vähän näytettäväksi',
  'ts.st.suppressed_near_unanimous': 'Piilotettu',
  'ts.st.ok': 'Näytetty',
  'ts.youExpected': (m) => `Odotit ${m}/5`,
  'ts.view': 'Näytä',

  'ts.sessionTitle': 'Kysely',
  'ts.conductTitle': (c) => `Kysely auki · ${c}`,
  'ts.reflectTitle': (c) => `Tulokset · ${c}`,
  'ts.projLead': 'Skannaa koodi tai mene osoitteeseen',
  'ts.timeLeft': (t) => `${t} jäljellä`,
  'ts.qrLabel': (code) => `QR-koodi kyselyn linkkiin, tunnus ${code}`,
  'ts.liveTitle': 'Vastauksia tähän mennessä',
  'ts.liveCount': (c) => n(c, 'vastaus', 'vastausta'),
  'ts.liveHint': 'Kyselyn ollessa auki näkyy vain määrä. Tulokset näkyvät, kun se päättyy.',
  'ts.endsAt': (t) => `Päättyy klo ${t}`,
  'ts.fullscreen': 'Koko näyttö',
  'ts.extend': (m) => `+ ${m} min`,
  'ts.extended': (t) => `Jatkettu klo ${t} asti.`,
  'ts.atLimit': (m) => `Kysely on koulusi ${m} minuutin enimmäisajassa.`,
  'ts.slideToEnd': 'Liu’uta lopettaaksesi kyselyn',
  'ts.slideToEndAria': 'Lopeta kysely. Paina kahdesti lopettaaksesi ilman liu’uttamista.',
  'ts.pressAgainToEnd': 'Paina uudelleen lopettaaksesi',
  'ts.closeIntro': (c) =>
    `${c} ${n(c, 'oppilas on', 'oppilasta on')} vastannut. Päättymisen jälkeen kukaan ei voi vastata, eikä kyselyä voi avata uudelleen.`,
  'ts.closedNow': 'Kysely päättyi.',

  'ts.resultsTitle': 'Mitä luokka vastasi',
  'ts.belowFloor': (total, floor) =>
    `${total}/${floor} vastausta tarvitaan. Liian vähän vastauksia näytettäväksi; ne lasketaan koulun kokonaismäärään.`,
  'ts.byTopic': 'Mieliala aiheittain',
  'ts.crossMean': (mean, total) => `keskiarvo ${mean.replace('.', ',')}/5 · ${total} vastausta`,
  'ts.crossHidden': 'piilotettu',
  'ts.crossNote': 'Aiheen mieliala näytetään vain, kun sekä tämä ryhmä että muut vastaajat ovat riittävän suuria näytettäväksi turvallisesti.',
  'ts.resultsWho': 'Vain sinä näet nämä tulokset. Rehtori ei koskaan näe yksittäistä luokkaa.',
  'ts.questionSet': (v) => `Kysymyssarja v${v}`,
  'ts.compareTitle': 'Ennusteesi ja tulos',
  'ts.noPredictionMade': 'Et tehnyt ennustetta tähän kyselyyn.',
  'ts.compareHidden': 'Tulos on piilotettu, joten vertailtavaa ei ole.',
  'ts.expected': 'Odotit',
  'ts.classSaid': 'Luokka vastasi',
  'ts.gap': 'Ero',
  'ts.gapClose': 'Lähellä odottamaasi',
  'ts.gapHigher': (d) => `Odotit ${d} parempaa`,
  'ts.gapLower': (d) => `Odotit ${d} heikompaa`,
  'ts.expectedTopics': 'Odottamasi aiheet',
  'ts.onlyYou': 'Vain sinä näet ennusteesi ja muistiinpanosi – ei rehtori, ei koulun ylläpitäjä eikä Zencera.',

  'ts.ysTitle': 'Te sanoitte, me teimme',
  'ts.ysLabel': 'Yksi rivi takaisin luokalle',
  'ts.ysPlaceholder': 'esim. Moni mainitsi kokeet, joten siirrämme perjantain testiä.',
  'ts.ysRule': 'Näytetään luokalle sen seuraavassa kyselyssä. Ei nimiä eikä mitään yksittäisestä oppilaasta.',
  'ts.charsLeft': (c) => `${c} merkkiä jäljellä.`,
  'ts.ysPost': 'Julkaise luokalle',
  'ts.ysPosted': 'Julkaistu. Luokka näkee sen seuraavassa kyselyssä.',
  'ts.ysShown': 'Luokka näkee tämän seuraavassa kyselyssään (enintään 30 päivää).',
  'ts.ysWithdraw': 'Peru',
  'ts.ysWithdrawn': 'Peruttu.',
  'ts.ysNotYet': 'Voit vastata, kun tämän kyselyn tulokset näytetään.',

  'ts.noteTitle': 'Omat muistiinpanot',
  'ts.noteLabel': 'Muistiinpanot itsellesi',
  'ts.noteWho': 'Vain sinä voit lukea tämän. Sitä ei jaeta kenellekään, ei edes Zenceran tuelle, ja se poistetaan, jos tilisi poistetaan.',
  'ts.noteSaved': 'Muistiinpano tallennettu.',
  'ts.save': 'Tallenna',

  'ts.calSessions': 'Verrattuja kyselyitä',
  'ts.calSessionsSub': 'Ennuste ja näytetyt tulokset',
  'ts.calGap': 'Keskimääräinen ero',
  'ts.calNone': 'Tee ennuste ennen seuraavaa kyselyäsi',
  'ts.calHigher': 'Odotat yleensä parempaa mielialaa kuin luokka kertoo',
  'ts.calLower': 'Odotat yleensä heikompaa mielialaa kuin luokka kertoo',
  'ts.calClose': 'Ennusteesi ovat lähellä luokkien vastauksia',
  'ts.calByContext': 'Tilanteen mukaan',
  'ts.calEmpty': 'Ei vielä vertailtavaa.',
  'ts.calN': (c) => n(c, '1 kysely', `${c} kyselyä`),
  'ts.calHow': 'Ero = odottamasi mieliala miinus luokan keskiarvo. + tarkoittaa, että odotit parempaa kuin luokka vastasi.',

  'ci.youSaid': 'Opettajaltasi',

  'err.not_your_class': 'Tämä luokka tai kysely ei ole sinun, tai et enää opeta luokkaa.',
  'err.class_busy': 'Kollegalla on luokalle kysely auki. Yritä uudelleen, kun se päättyy.',
  'err.session_finished': 'Tämä kysely on jo päättynyt.',
  'err.session_open': 'Tulokset näkyvät, kun kysely on päättynyt.',
  'err.session_limit': 'Kysely on jo koulusi enimmäisajassa.',
  'err.results_not_shown': 'Voit vastata, kun tämän kyselyn tulokset näytetään.',
  'err.bad_length': 'Kirjoita 3–200 merkkiä.',
  'err.bad_content': 'Vain tavallista tekstiä: ei linkkejä, sähköpostiosoitteita tai tageja.',
  'err.already_posted': 'Olet jo vastannut tähän kyselyyn.',
  'err.feature_off': 'Tämä toiminto on toistaiseksi pois käytöstä.',
  'err.too_many': 'Sinulla on enimmäismäärä aikatauluja. Poista ensin yksi.',
};

export const teacherSv: TeacherStrings = {
  'ts.navCalibration': 'Dina förutsägelser',
  'ts.classesSub': 'Klassföreståndar- och ämnesklasser',
  'ts.openSub': 'Startade av dig',
  'ts.noClasses': 'Du har inga klasser ännu. Skolans administratör tilldelar dem.',
  'ts.whoSees':
    'Resultaten visas per incheckning och bara du ser incheckningarna du håller. Rektorn ser bara årskurs- och skolsammanställningar, aldrig en enskild klass.',
  'ts.kind': 'Klass',
  'ts.homeroom': 'Egen klass',
  'ts.subject': 'Ämnesklass',
  'ts.size': 'Antal elever',
  'ts.students': (c) => n(c, '1 elev', `${c} elever`),
  'ts.fc.likely': 'Resultaten visas troligen',
  'ts.fc.uncertain': 'Kanske för få',
  'ts.fc.unlikely': 'För få för att visa resultat',
  'ts.fc.unknown': 'Ingen prognos',
  'ts.fcHint.likely': 'Vanligtvis svarar tillräckligt många för att resultaten ska visas.',
  'ts.fcHint.uncertain': 'Resultaten visas från 5 svar. Uppmuntra alla att delta.',
  'ts.fcHint.unlikely': 'Färre än 5 svar är troligt. De räknas ändå in i skolans totalsumma.',
  'ts.fcHint.unknown': 'Lägg till klassens storlek för att få en prognos.',
  'ts.fcTitle': 'Deltagarprognos',
  'ts.fcHow': 'Baserad på klassens storlek och hur många som svarade på dina senaste incheckningar här.',
  'ts.openUntil': (code, time) => `Öppen · kod ${code} · till kl. ${time}`,
  'ts.colleagueOpen': 'En kollega har en incheckning öppen för klassen.',
  'ts.openProjector': 'Öppna projektorvy',
  'ts.plan': 'Planera incheckning',
  'ts.lastResults': 'Senaste resultat',
  'ts.classPage': 'Klassens sida',
  'ts.dueTitle': 'Schemalagt nu',
  'ts.day1': 'Måndag',
  'ts.day2': 'Tisdag',
  'ts.day3': 'Onsdag',
  'ts.day4': 'Torsdag',
  'ts.day5': 'Fredag',
  'ts.day6': 'Lördag',
  'ts.day7': 'Söndag',
  'ts.minutes': (m) => `${m} min`,

  'ts.classTitle': (c) => `Klass ${c}`,
  'ts.classTitleBare': 'Klass',
  'ts.classGone': 'Den här klassen finns inte på din lista. Den kan ha flyttats till en annan lärare.',
  'ts.planTitle': 'Planera en incheckning',
  'ts.length': 'Längd',
  'ts.tag': 'Vad som händer den här veckan',
  'ts.tagNone': 'Inget särskilt',
  'ts.tag.normal': 'Vanlig vecka',
  'ts.tag.exam_week': 'Provvecka',
  'ts.tag.after_holiday': 'Efter ett lov',
  'ts.tag.after_incident': 'Efter en händelse',
  'ts.tag.trip_event': 'Utflykt eller evenemang',
  'ts.tagHint': 'Hjälper dig att tolka resultaten senare. Rektorn ser bara hur ofta var och en används i hela skolan.',
  'ts.predictTitle': 'Innan du börjar: hur tror du att klassen mår?',
  'ts.predictTitleShort': 'Din förutsägelse',
  'ts.predictHint': 'Valfritt. Bara du ser den. Du jämför den med klassens svar efteråt.',
  'ts.predictMood': 'Förväntat humör',
  'ts.predictTopics': 'Ämnen du förväntar dig',
  'ts.predictClear': 'Ta bort förutsägelsen',
  'ts.predictAdd': 'Lägg till en förutsägelse',
  'ts.predictChange': 'Ändra förutsägelsen',
  'ts.noPrediction': 'Ingen förutsägelse ännu.',
  'ts.draft': 'Utkast',
  'ts.draftSet': (label) => `Frågeuppsättningen ”${label}” är ett utkast och har inte granskats ännu.`,

  'ts.schedTitle': 'Återkommande incheckning',
  'ts.schedHint': 'En påminnelse på din startsida vid den tiden. Du startar ändå varje incheckning själv.',
  'ts.schedDay': 'Dag',
  'ts.schedTime': 'Starttid',
  'ts.schedSave': '+ Lägg till schema',
  'ts.schedSaved': 'Schemat har lagts till.',
  'ts.schedEnd': 'Ta bort',
  'ts.schedEnded': 'Schemat har tagits bort.',

  'ts.histTitle': 'Dina tidigare incheckningar',
  'ts.histHint': 'Bara incheckningar du har hållit. Var och en visas för sig.',
  'ts.histNone': 'Inga avslutade incheckningar ännu.',
  'ts.st.insufficient': 'För få för att visas',
  'ts.st.suppressed_near_unanimous': 'Dold',
  'ts.st.ok': 'Visad',
  'ts.youExpected': (m) => `Du förväntade dig ${m}/5`,
  'ts.view': 'Visa',

  'ts.sessionTitle': 'Incheckning',
  'ts.conductTitle': (c) => `Incheckning öppen · ${c}`,
  'ts.reflectTitle': (c) => `Resultat · ${c}`,
  'ts.projLead': 'Skanna koden eller gå till',
  'ts.timeLeft': (t) => `${t} kvar`,
  'ts.qrLabel': (code) => `QR-kod till incheckningslänken, kod ${code}`,
  'ts.liveTitle': 'Svar hittills',
  'ts.liveCount': (c) => n(c, 'svar', 'svar'),
  'ts.liveHint': 'Medan incheckningen är öppen visas bara antalet. Resultaten visas när den har avslutats.',
  'ts.endsAt': (t) => `Avslutas kl. ${t}`,
  'ts.fullscreen': 'Helskärm',
  'ts.extend': (m) => `+ ${m} min`,
  'ts.extended': (t) => `Förlängd till kl. ${t}.`,
  'ts.atLimit': (m) => `Incheckningen har nått skolans gräns på ${m} minuter.`,
  'ts.slideToEnd': 'Dra för att avsluta incheckningen',
  'ts.slideToEndAria': 'Avsluta incheckningen. Tryck två gånger för att avsluta utan att dra.',
  'ts.pressAgainToEnd': 'Tryck igen för att avsluta',
  'ts.closeIntro': (c) =>
    `${c} ${n(c, 'elev har', 'elever har')} svarat. Ingen kan svara efter att den har avslutats, och den kan inte öppnas igen.`,
  'ts.closedNow': 'Incheckningen har avslutats.',

  'ts.resultsTitle': 'Vad klassen svarade',
  'ts.belowFloor': (total, floor) =>
    `${total} av ${floor} svar behövs. För få svar för att visas; de räknas in i skolans totalsumma.`,
  'ts.byTopic': 'Humör per ämne',
  'ts.crossMean': (mean, total) => `medelvärde ${mean.replace('.', ',')}/5 · ${total} svar`,
  'ts.crossHidden': 'dold',
  'ts.crossNote': 'Ett ämnes humör visas bara när både den gruppen och alla andra är tillräckligt stora för att visas säkert.',
  'ts.resultsWho': 'Bara du ser de här resultaten. Rektorn ser aldrig en enskild klass.',
  'ts.questionSet': (v) => `Frågeuppsättning v${v}`,
  'ts.compareTitle': 'Din förutsägelse och resultatet',
  'ts.noPredictionMade': 'Du gjorde ingen förutsägelse för den här incheckningen.',
  'ts.compareHidden': 'Resultatet är dolt, så det finns inget att jämföra.',
  'ts.expected': 'Du förväntade dig',
  'ts.classSaid': 'Klassen svarade',
  'ts.gap': 'Skillnad',
  'ts.gapClose': 'Nära det du förväntade dig',
  'ts.gapHigher': (d) => `Du förväntade dig ${d} högre`,
  'ts.gapLower': (d) => `Du förväntade dig ${d} lägre`,
  'ts.expectedTopics': 'Ämnen du förväntade dig',
  'ts.onlyYou': 'Bara du ser dina förutsägelser och anteckningar – inte rektorn, inte skolans administratör och inte Zencera.',

  'ts.ysTitle': 'Ni sa, vi gjorde',
  'ts.ysLabel': 'En rad tillbaka till klassen',
  'ts.ysPlaceholder': 't.ex. Många av er nämnde proven, så vi flyttar fredagens förhör.',
  'ts.ysRule': 'Visas för klassen vid nästa incheckning. Inga namn och inget om en enskild elev.',
  'ts.charsLeft': (c) => `${c} tecken kvar.`,
  'ts.ysPost': 'Publicera för klassen',
  'ts.ysPosted': 'Publicerat. Klassen ser det vid nästa incheckning.',
  'ts.ysShown': 'Klassen ser detta vid sin nästa incheckning (i högst 30 dagar).',
  'ts.ysWithdraw': 'Dra tillbaka',
  'ts.ysWithdrawn': 'Tillbakadraget.',
  'ts.ysNotYet': 'Du kan svara när resultaten för den här incheckningen visas.',

  'ts.noteTitle': 'Privata anteckningar',
  'ts.noteLabel': 'Anteckningar för dig själv',
  'ts.noteWho': 'Bara du kan läsa detta. Det delas inte med någon, inte ens Zenceras support, och raderas om ditt konto tas bort.',
  'ts.noteSaved': 'Anteckningen har sparats.',
  'ts.save': 'Spara',

  'ts.calSessions': 'Jämförda incheckningar',
  'ts.calSessionsSub': 'Med förutsägelse och visade resultat',
  'ts.calGap': 'Genomsnittlig skillnad',
  'ts.calNone': 'Gör en förutsägelse före din nästa incheckning',
  'ts.calHigher': 'Du förväntar dig oftast bättre humör än klassen uppger',
  'ts.calLower': 'Du förväntar dig oftast sämre humör än klassen uppger',
  'ts.calClose': 'Dina förutsägelser ligger nära klassernas svar',
  'ts.calByContext': 'Efter vad som pågick',
  'ts.calEmpty': 'Inget att jämföra ännu.',
  'ts.calN': (c) => n(c, '1 incheckning', `${c} incheckningar`),
  'ts.calHow': 'Skillnad = ditt förväntade humör minus klassens medelvärde. + betyder att du förväntade dig bättre än klassen svarade.',

  'ci.youSaid': 'Från din lärare',

  'err.not_your_class': 'Den här klassen eller incheckningen är inte din, eller så undervisar du inte längre klassen.',
  'err.class_busy': 'En kollega har en incheckning öppen för klassen. Försök igen när den har avslutats.',
  'err.session_finished': 'Den här incheckningen har redan avslutats.',
  'err.session_open': 'Resultaten visas när incheckningen har avslutats.',
  'err.session_limit': 'Incheckningen har redan nått skolans tidsgräns.',
  'err.results_not_shown': 'Du kan svara när resultaten för den här incheckningen visas.',
  'err.bad_length': 'Skriv 3–200 tecken.',
  'err.bad_content': 'Bara vanlig text: inga länkar, e-postadresser eller taggar.',
  'err.already_posted': 'Du har redan svarat på den här incheckningen.',
  'err.feature_off': 'Den här funktionen är avstängd tills vidare.',
  'err.too_many': 'Du har det högsta antalet scheman. Ta först bort ett.',
};
